/** localStorage key for the Special features Zoom / Scale size (%). */
export const APP_ZOOM_STORAGE_KEY = 'fontSize';
/** When set, responsive defaults no longer override the user's Scale size. */
export const APP_ZOOM_USER_OVERRIDE_KEY = 'fontSizeUserOverride';

/**
 * Recommended Special features Zoom / Scale size (%) from viewport width.
 * >1500 → 100%; ~1352 → 90%; smaller viewports step down further.
 */
export function getRecommendedAppZoomPercent(viewportWidth = window.innerWidth): number {
  if (viewportWidth >= 1500) {
    return 100;
  }
  if (viewportWidth >= 1350) {
    return 90;
  }
  if (viewportWidth >= 1200) {
    return 85;
  }
  if (viewportWidth >= 1024) {
    return 80;
  }
  return 75;
}

export function applyAppZoomPercent(percent: number): void {
  const clamped = Math.min(300, Math.max(50, Math.round(percent)));
  const scale = clamped / 100;
  document.documentElement.style.setProperty('--app-zoom', String(scale));
  (document.body.style as CSSStyleDeclaration & {zoom?: string}).zoom = String(scale);
}

export function readStoredAppZoomPercent(): number | null {
  const raw = localStorage.getItem(APP_ZOOM_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function isAppZoomUserOverride(): boolean {
  return localStorage.getItem(APP_ZOOM_USER_OVERRIDE_KEY) === '1';
}

export function setAppZoomUserOverride(enabled: boolean): void {
  if (enabled) {
    localStorage.setItem(APP_ZOOM_USER_OVERRIDE_KEY, '1');
  } else {
    localStorage.removeItem(APP_ZOOM_USER_OVERRIDE_KEY);
  }
}

export function persistAppZoomPercent(percent: number, userOverride = false): void {
  localStorage.setItem(APP_ZOOM_STORAGE_KEY, String(Math.round(percent)));
  if (userOverride) {
    setAppZoomUserOverride(true);
  }
}

/**
 * Apply Zoom / Scale on app load (and optionally on resize):
 * use saved user override if present, otherwise the screen-size recommendation.
 */
export function syncResponsiveAppZoom(options?: {forceRecommended?: boolean}): number {
  const forceRecommended = options?.forceRecommended === true;
  if (!forceRecommended && isAppZoomUserOverride()) {
    const stored = readStoredAppZoomPercent();
    const percent = stored ?? 100;
    applyAppZoomPercent(percent);
    return percent;
  }
  if (forceRecommended) {
    setAppZoomUserOverride(false);
  }
  const recommended = getRecommendedAppZoomPercent();
  applyAppZoomPercent(recommended);
  persistAppZoomPercent(recommended, false);
  return recommended;
}
